/**
 * Extracts a flat address object from a Google Maps Geocoding result or
 * a Google Places Autocomplete `getPlace()` result.
 *
 * @param {google.maps.GeocoderResult | google.maps.places.PlaceResult} place
 * @returns {{ addressLine1: string, city: string, state: string, pincode: string }}
 */
export const extractAddressFromPlace = (place) => {
  if (!place || !place.address_components) return {};

  const get = (type) =>
    place.address_components.find((c) => c.types.includes(type))?.long_name ||
    "";

  return {
    addressLine1: place.formatted_address || "",
    city: get("locality"),
    state: get("administrative_area_level_1"),
    pincode: get("postal_code"),
  };
};

/**
 * Reverse-geocodes an arbitrary { lat, lng } pair via the Google Geocoding REST API.
 * Used when the user drags the map marker to a custom location.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {string} googleMapsApiKey
 * @returns {Promise<{ addressLine1: string, city: string, state: string, pincode: string, lat: number, lng: number }>}
 * @throws {Error}
 */
export const reverseGeocodeLatLng = async (lat, lng, googleMapsApiKey) => {
  const res = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`,
  );
  const data = await res.json();
  const result = data.results?.[0];
  if (!result) throw new Error("No address found for the selected location");
  return {
    ...extractAddressFromPlace(result),
    lat,
    lng,
  };
};

/**
 * Promisified wrapper around `navigator.geolocation.getCurrentPosition`.
 * Resolves with { latitude, longitude }.
 *
 * @returns {Promise<{ latitude: number, longitude: number }>}
 * @throws {Error}
 */
export const getCurrentCoords = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation is not supported by your browser"));
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => reject(new Error("Location permission denied")),
    );
  });

/**
 * Uses the browser Geolocation API to get the current coordinates, then
 * reverse-geocodes them via the Google Geocoding API and returns a flat
 * address object (also includes lat/lng for map centering).
 *
 * @param {string} googleMapsApiKey  – your REACT_APP_GOOGLE_MAPS_API_KEY
 * @returns {Promise<{ addressLine1: string, city: string, state: string, pincode: string, lat: number, lng: number }>}
 * @throws {Error} with a human-readable `.message` on any failure
 */
export const fetchAddressFromCoords = async (googleMapsApiKey) => {
  const { latitude, longitude } = await getCurrentCoords();
  return reverseGeocodeLatLng(latitude, longitude, googleMapsApiKey);
};
